<template>
  <nav class="modern-menu" role="navigation">
  <div class="modern-menu__inner-container">
    <div class="modern-menu__more-container" ref="moreButtonContainer" v-if="currentMegaMenuRubriques.length > 0" @mouseenter="handleMoreContainerEnter" @mouseleave="handleMoreContainerLeave">
      <button @click="toggleMegaMenu" class="modern-menu__more-button" :class="{ 'active-hover-style': isMegaMenuOpen || isHoveringMoreButton }" :aria-expanded="isMegaMenuOpen.toString()" aria-controls="mega-menu-content">
        <span class="menu-icon"><i class="fas fa-bars"></i></span> Toutes
      </button>
      <div v-show="isMegaMenuOpen || isHoveringMoreButton" id="mega-menu-content" class="modern-menu__mega-menu">
        <div v-for="rubrique in allRawRubriques" :key="rubrique.id || rubrique.title" class="modern-menu__mega-menu-rubrique">
          <h3><a :href="rubrique.path">{{ rubrique.title }}</a></h3>
          <ul v-if="rubrique.categories && rubrique.categories.length > 0">
            <li v-for="category in rubrique.categories" :key="category.id || category.title">
              <a :href="category.path">{{ category.title }}</a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <ul class="modern-menu__visible-rubriques" ref="visibleRubriquesContainer">
      <li v-for="(rubrique, index) in currentVisibleRubriques" :key="rubrique.id || rubrique.title" ref="rubriqueLiElements">
        <a :href="rubrique.path">{{ rubrique.title }}</a>
        <!-- Simple dropdown for categories of visible rubriques for now -->
        <ul v-if="rubrique.categories && rubrique.categories.length > 0" class="modern-menu__simple-dropdown">
          <li v-for="category in rubrique.categories" :key="category.id || category.title">
            <a :href="category.path">{{ category.title }}</a>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</nav>
</template>

<script>
export default {
  name: 'ModernMenu',
  props: {
    rawRubriques: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      isMegaMenuOpen: false,
      isHoveringMoreButton: false,
      currentVisibleRubriques: [],
      currentMegaMenuRubriques: [],
      // rubriqueRefs data property removed, will use this.$refs directly
      cachedMoreButtonWidth: 0,
      resizeTimeout: null,
    };
  },
  computed: {
    allRawRubriques() { // Renamed to avoid conflict if we need 'allRubriques' for something else
      return this.rawRubriques;
    },
    // visibleRubriques and megaMenuRubriques are now direct data properties (currentVisibleRubriques, currentMegaMenuRubriques)
  },
  methods: {
    toggleMegaMenu() {
      this.isMegaMenuOpen = !this.isMegaMenuOpen;
      // If opened by click, ensure hover state is also considered true initially
      if (this.isMegaMenuOpen) {
        this.isHoveringMoreButton = true;
      }
    },
    handleClickOutside(event) {
      if (this.isMegaMenuOpen && this.$el && !this.$el.contains(event.target)) {
        // Check if the click was on the button itself, if so, toggleMegaMenu handles it
        const moreButton = this.$el.querySelector('.modern-menu__more-button');
        if (moreButton && !moreButton.contains(event.target)) {
            this.isMegaMenuOpen = false;
            this.isHoveringMoreButton = false; // Also reset hover state
        }
      }
    },
    handleMoreContainerEnter() {
      this.isHoveringMoreButton = true;
    },
    handleMoreContainerLeave() {
      // Only hide on mouse leave if not pinned open by a click
      if (!this.isMegaMenuOpen) {
        this.isHoveringMoreButton = false;
      }
    },
    calculateVisibleRubriques() {
      if (!this.$refs.visibleRubriquesContainer || !this.allRawRubriques) return;

      // Ensure all items are in currentVisibleRubriques to get their refs for measurement
      // and to allow the UL container to have its full potential width before we measure it.
      this.currentVisibleRubriques = [...this.allRawRubriques];
      this.currentMegaMenuRubriques = []; // Hide 'Tous' button for this measurement pass

      this.$nextTick(() => { // This $nextTick waits for the DOM to update with all rubriques in currentVisibleRubriques
        const containerWidth = this.$refs.visibleRubriquesContainer.offsetWidth;

        // Pass 1: Assume 'Tous' button is not needed, calculate fit.
        const availableWidthPass1 = containerWidth;
        let occupiedWidthPass1 = 0;
        const tempVisiblePass1 = [];
        const tempMegaPass1 = [];

        const itemElements = this.$refs.rubriqueLiElements || [];
        if (itemElements.length !== this.allRawRubriques.length) {
          if (this.allRawRubriques.length === 0) {
              this.currentVisibleRubriques = [];
              this.currentMegaMenuRubriques = [];
          }
          // Consider if a retry is needed or if this state is acceptable (e.g. on rapid data changes)
          return;
        }

        for (let i = 0; i < this.allRawRubriques.length; i++) {
          const itemElement = itemElements[i];
          const rubrique = this.allRawRubriques[i];
          if (!itemElement) {
            tempMegaPass1.push(rubrique);
            continue;
          }
          const itemStyle = getComputedStyle(itemElement);
          const itemWidth = itemElement.offsetWidth + parseInt(itemStyle.marginLeft) + parseInt(itemStyle.marginRight);

          if (occupiedWidthPass1 + itemWidth <= availableWidthPass1 || tempVisiblePass1.length === 0) {
            occupiedWidthPass1 += itemWidth;
            tempVisiblePass1.push(rubrique);
          } else {
            tempMegaPass1.push(rubrique);
          }
        }

        // Pass 2: If Pass 1 had overflow, recalculate with 'Tous' button width.
        if (tempMegaPass1.length > 0) {
          const availableWidthPass2 = containerWidth - this.cachedMoreButtonWidth;
          let occupiedWidthPass2 = 0;
          const finalVisible = [];
          const finalMega = []; // tempMegaPass1 is not used directly, re-evaluate all items.

          // Item elements refs should still be valid from the initial full render
          for (let i = 0; i < this.allRawRubriques.length; i++) {
            const itemElement = itemElements[i];
            const rubrique = this.allRawRubriques[i];
            if (!itemElement) {
              finalMega.push(rubrique);
              continue;
            }
            const itemStyle = getComputedStyle(itemElement);
            const itemWidth = itemElement.offsetWidth + parseInt(itemStyle.marginLeft) + parseInt(itemStyle.marginRight);

            if (occupiedWidthPass2 + itemWidth <= availableWidthPass2 || finalVisible.length === 0) {
              occupiedWidthPass2 += itemWidth;
              finalVisible.push(rubrique);
            } else {
              finalMega.push(rubrique);
            }
          }
          this.currentVisibleRubriques = finalVisible;
          this.currentMegaMenuRubriques = finalMega;
        } else {
          // No 'Tous' button needed, Pass 1 results are final.
          this.currentVisibleRubriques = tempVisiblePass1;
          this.currentMegaMenuRubriques = []; // Ensure mega menu is empty
        }
      });
    },
    handleResize() {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        this.calculateVisibleRubriques();
      }, 200); // Debounce for 200ms
    }
  },
  mounted() {
    // Cache the 'Tous' button width first
    const originalMegaRubriques = this.currentMegaMenuRubriques; // Should be empty array initially
    this.currentMegaMenuRubriques = [{ id: 'dummyMeasure', title: 'Dummy' }]; // Temporarily populate to ensure button is rendered by v-if

    this.$nextTick(() => {
      if (this.$refs.moreButtonContainer) {
        const style = getComputedStyle(this.$refs.moreButtonContainer);
        this.cachedMoreButtonWidth = this.$refs.moreButtonContainer.offsetWidth + parseInt(style.marginLeft) + parseInt(style.marginRight);
      } else {
        this.cachedMoreButtonWidth = 80; // Fallback estimate if ref not found
      }
      // Restore mega rubriques (likely to empty for initial calc)
      this.currentMegaMenuRubriques = originalMegaRubriques;

      // Now that cachedMoreButtonWidth is set, perform the initial layout calculation
      this.$nextTick(() => { // Ensure DOM is updated after restoring currentMegaMenuRubriques
          this.calculateVisibleRubriques();
      });

      window.addEventListener('resize', this.handleResize);
      document.addEventListener('click', this.handleClickOutside, true);
    });
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside, true);
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }
};
</script>
